import React from 'react';
import { Card, CardBody, CardHeader } from '@heroui/react';
import { motion } from 'framer-motion';
import { CompletionFunnel } from '@/services/apis/analytics';
import { useTranslation } from 'react-i18next';

interface CompletionFunnelChartProps {
  data: CompletionFunnel;
}

const CompletionFunnelChart: React.FC<CompletionFunnelChartProps> = ({ data }) => {
  const { t } = useTranslation('admin');

  const maxValue = Math.max(data.enrolled, 1);
  const stages = [
    { label: t('dashboard.funnel.enrolled'), value: data.enrolled, color: 'bg-blue-500' },
    { label: t('dashboard.funnel.started'), value: data.started, color: 'bg-indigo-500' },
    { label: t('dashboard.funnel.inProgress'), value: data.inProgress, color: 'bg-purple-500' },
    { label: t('dashboard.funnel.completed'), value: data.completed, color: 'bg-emerald-500' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
      <Card className="h-full">
        <CardHeader className="pb-2">
          <h3 className="text-lg font-semibold text-foreground">{t('dashboard.completionFunnel')}</h3>
        </CardHeader>
        <CardBody className="pt-2">
          <div className="space-y-4">
            {stages.map((stage, index) => {
              const width = maxValue > 0 ? (stage.value / maxValue) * 100 : 0;
              return (
                <div key={stage.label} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-default-600">{stage.label}</span>
                    <span className="font-medium text-foreground">{stage.value}</span>
                  </div>
                  <div className="h-6 bg-default-100 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full ${stage.color} rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default CompletionFunnelChart;

